/**
 * Image Editor vector path (Pen tool) — v1.
 *
 * Transient editable path (anchors + cubic handles). Not a persistent layer
 * type: close → Path→Selection, or rasterize → float → raster layer (Text-like).
 */

/** Same snap radius as polygonal lasso close-to-start. */
export const PATH_CLOSE_SNAP_PX = 14;

/**
 * @typedef {{
 *   x: number,
 *   y: number,
 *   ox: number,
 *   oy: number,
 *   ix: number,
 *   iy: number,
 * }} PathAnchor
 */

/**
 * @typedef {{
 *   anchors: PathAnchor[],
 *   closed: boolean,
 * }} EditorPath
 */

/**
 * @returns {EditorPath}
 */
export function createEmptyEditorPath() {
  return { anchors: [], closed: false };
}

/**
 * @param {number} x
 * @param {number} y
 * @param {{ ox?: number, oy?: number, ix?: number, iy?: number }} [handles]
 * @returns {PathAnchor}
 */
export function createPathAnchor(x, y, handles = {}) {
  return {
    x: Number(x) || 0,
    y: Number(y) || 0,
    ox: Number(handles.ox) || 0,
    oy: Number(handles.oy) || 0,
    ix: Number(handles.ix) || 0,
    iy: Number(handles.iy) || 0,
  };
}

/**
 * @param {EditorPath|null|undefined} path
 * @returns {EditorPath}
 */
export function cloneEditorPath(path) {
  if (!path?.anchors) return createEmptyEditorPath();
  return {
    closed: Boolean(path.closed),
    anchors: path.anchors.map((a) => ({
      x: a.x,
      y: a.y,
      ox: a.ox,
      oy: a.oy,
      ix: a.ix,
      iy: a.iy,
    })),
  };
}

/**
 * @param {EditorPath} path
 * @param {number} x
 * @param {number} y
 * @param {number} [snapPx]
 * @returns {boolean}
 */
export function pathNearFirstAnchor(path, x, y, snapPx = PATH_CLOSE_SNAP_PX) {
  if (!path?.anchors?.length || path.closed) return false;
  if (path.anchors.length < 3) return false;
  const a = path.anchors[0];
  return Math.hypot(x - a.x, y - a.y) <= snapPx;
}

/**
 * Append a corner anchor (handles zero until drag).
 * @param {EditorPath} path
 * @param {number} x
 * @param {number} y
 * @returns {EditorPath}
 */
export function appendPathAnchor(path, x, y) {
  const next = cloneEditorPath(path);
  if (next.closed) return next;
  next.anchors.push(createPathAnchor(x, y));
  return next;
}

/**
 * After placing an anchor, drag sets symmetric smooth handles on the last anchor.
 * @param {EditorPath} path
 * @param {number} pointerX
 * @param {number} pointerY
 * @returns {EditorPath}
 */
export function updateLastAnchorDragHandles(path, pointerX, pointerY) {
  const next = cloneEditorPath(path);
  if (!next.anchors.length || next.closed) return next;
  const last = next.anchors[next.anchors.length - 1];
  const dx = pointerX - last.x;
  const dy = pointerY - last.y;
  last.ox = dx;
  last.oy = dy;
  last.ix = -dx;
  last.iy = -dy;
  return next;
}

/**
 * @param {EditorPath} path
 * @returns {EditorPath|null} closed path, or null if too few anchors
 */
export function closeEditorPath(path) {
  const next = cloneEditorPath(path);
  if (next.anchors.length < 3) return null;
  next.closed = true;
  return next;
}

/**
 * Cubic Bézier point.
 * @param {{x:number,y:number}} p0
 * @param {{x:number,y:number}} p1
 * @param {{x:number,y:number}} p2
 * @param {{x:number,y:number}} p3
 * @param {number} t
 * @returns {{x:number,y:number}}
 */
export function cubicAt(p0, p1, p2, p3, t) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;
  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y,
  };
}

/**
 * @param {PathAnchor} a
 * @param {PathAnchor} b
 * @returns {boolean}
 */
function segmentIsLinear(a, b) {
  return a.ox === 0 && a.oy === 0 && b.ix === 0 && b.iy === 0;
}

/**
 * Sample one segment from a → b into polyline points (excludes `a`, includes samples through `b`).
 * @param {PathAnchor} a
 * @param {PathAnchor} b
 * @param {number} [steps]
 * @returns {Array<{x:number,y:number}>}
 */
export function samplePathSegment(a, b, steps = 20) {
  if (segmentIsLinear(a, b)) {
    return [{ x: b.x, y: b.y }];
  }
  const p0 = { x: a.x, y: a.y };
  const p1 = { x: a.x + a.ox, y: a.y + a.oy };
  const p2 = { x: b.x + b.ix, y: b.y + b.iy };
  const p3 = { x: b.x, y: b.y };
  const n = Math.max(4, Math.round(steps));
  /** @type {Array<{x:number,y:number}>} */
  const out = [];
  for (let i = 1; i <= n; i += 1) {
    out.push(cubicAt(p0, p1, p2, p3, i / n));
  }
  return out;
}

/**
 * Flatten path to polyline for fill / selection (includes all anchors + curve samples).
 * @param {EditorPath} path
 * @param {{ steps?: number }} [opts]
 * @returns {Array<{x:number,y:number}>}
 */
export function sampleEditorPathPolyline(path, opts = {}) {
  const anchors = path?.anchors;
  if (!Array.isArray(anchors) || anchors.length === 0) return [];
  const steps = opts.steps == null ? 20 : opts.steps;
  /** @type {Array<{x:number,y:number}>} */
  const pts = [{ x: anchors[0].x, y: anchors[0].y }];
  for (let i = 0; i < anchors.length - 1; i += 1) {
    pts.push(...samplePathSegment(anchors[i], anchors[i + 1], steps));
  }
  if (path.closed && anchors.length >= 3) {
    pts.push(...samplePathSegment(anchors[anchors.length - 1], anchors[0], steps));
  }
  return pts;
}

/**
 * SVG path `d` in canvas pixel space (caller multiplies by zoom when rendering).
 * @param {EditorPath} path
 * @param {{ hover?: {x:number,y:number}|null }} [opts]
 * @returns {string}
 */
export function editorPathToSvgD(path, opts = {}) {
  const anchors = path?.anchors;
  if (!Array.isArray(anchors) || anchors.length === 0) return '';
  const parts = [`M ${anchors[0].x} ${anchors[0].y}`];
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const a = anchors[i];
    const b = anchors[i + 1];
    if (segmentIsLinear(a, b)) {
      parts.push(`L ${b.x} ${b.y}`);
    } else {
      parts.push(
        `C ${a.x + a.ox} ${a.y + a.oy} ${b.x + b.ix} ${b.y + b.iy} ${b.x} ${b.y}`,
      );
    }
  }
  if (path.closed && anchors.length >= 3) {
    const a = anchors[anchors.length - 1];
    const b = anchors[0];
    if (segmentIsLinear(a, b)) {
      parts.push(`L ${b.x} ${b.y}`);
    } else {
      parts.push(
        `C ${a.x + a.ox} ${a.y + a.oy} ${b.x + b.ix} ${b.y + b.iy} ${b.x} ${b.y}`,
      );
    }
    parts.push('Z');
  } else if (opts.hover && !path.closed) {
    const last = anchors[anchors.length - 1];
    parts.push(`L ${opts.hover.x} ${opts.hover.y}`);
    void last;
  }
  return parts.join(' ');
}

/**
 * Ray-cast fill of a closed polyline into a binary mask.
 * @param {number} width
 * @param {number} height
 * @param {Array<{x:number,y:number}>} points
 * @returns {{ width: number, height: number, mask: Uint8Array }}
 */
function polylineToMask(width, height, points) {
  const w = Math.max(1, Math.floor(width) || 1);
  const h = Math.max(1, Math.floor(height) || 1);
  const mask = new Uint8Array(w * h);
  if (!Array.isArray(points) || points.length < 3) {
    return { width: w, height: h, mask };
  }
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.max(0, Math.floor(Math.min(...xs)));
  const maxX = Math.min(w - 1, Math.ceil(Math.max(...xs)));
  const minY = Math.max(0, Math.floor(Math.min(...ys)));
  const maxY = Math.min(h - 1, Math.ceil(Math.max(...ys)));
  const n = points.length;
  for (let y = minY; y <= maxY; y += 1) {
    const row = y * w;
    for (let x = minX; x <= maxX; x += 1) {
      let inside = false;
      for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
        const xi = points[i].x;
        const yi = points[i].y;
        const xj = points[j].x;
        const yj = points[j].y;
        const intersect =
          (yi > y) !== (yj > y) &&
          x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0000001) + xi;
        if (intersect) inside = !inside;
      }
      if (inside) mask[row + x] = 1;
    }
  }
  return { width: w, height: h, mask };
}

/**
 * Closed path → selection mask (samples cubics, then polygon fill).
 * @param {number} width
 * @param {number} height
 * @param {EditorPath} path
 * @returns {{ width: number, height: number, mask: Uint8Array }|null}
 */
export function pathToSelectionMask(width, height, path) {
  if (!path?.closed || !path.anchors || path.anchors.length < 3) return null;
  const poly = sampleEditorPathPolyline(path, { steps: 24 });
  return polylineToMask(width, height, poly);
}

/**
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }}
 */
function hexToRgb(hex) {
  const raw = typeof hex === 'string' ? hex.trim() : '';
  const m = /^#?([0-9a-f]{6})$/i.exec(raw);
  if (!m) return { r: 0, g: 0, b: 0 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Rasterize a closed filled path to a tight ImageData (Text-like float source).
 * Prefers offscreen canvas Path2D when available; falls back to sampled
 * polygon fill (Node smokes / no-DOM).
 *
 * @param {EditorPath} path
 * @param {{
 *   color?: string,
 *   padding?: number,
 * }} [opts]
 * @returns {{ imageData: ImageData, offsetX: number, offsetY: number }|null}
 */
export function rasterizePathToImageData(path, opts = {}) {
  if (!path?.closed || !path.anchors || path.anchors.length < 3) return null;
  if (typeof document !== 'undefined') {
    const viaCanvas = rasterizePathViaCanvas(path, opts);
    if (viaCanvas) return viaCanvas;
  }
  return rasterizePathViaSample(path, opts);
}

/**
 * @param {EditorPath} path
 * @param {{ color?: string, padding?: number }} opts
 * @returns {{ imageData: ImageData, offsetX: number, offsetY: number }|null}
 */
function rasterizePathViaCanvas(path, opts) {
  const poly = sampleEditorPathPolyline(path, { steps: 24 });
  if (poly.length < 3) return null;
  const pad = Math.max(0, Math.round(Number(opts.padding) || 2));
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  const minX = Math.floor(Math.min(...xs)) - pad;
  const minY = Math.floor(Math.min(...ys)) - pad;
  const maxX = Math.ceil(Math.max(...xs)) + pad;
  const maxY = Math.ceil(Math.max(...ys)) + pad;
  const w = Math.max(1, maxX - minX + 1);
  const h = Math.max(1, maxY - minY + 1);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.translate(-minX, -minY);
  const d = editorPathToSvgD(path);
  const p2d = new Path2D(d);
  ctx.fillStyle = opts.color || '#000000';
  ctx.fill(p2d);
  return {
    imageData: ctx.getImageData(0, 0, w, h),
    offsetX: minX,
    offsetY: minY,
  };
}

/**
 * @param {EditorPath} path
 * @param {{ color?: string, padding?: number }} opts
 * @returns {{ imageData: ImageData, offsetX: number, offsetY: number }|null}
 */
function rasterizePathViaSample(path, opts) {
  const poly = sampleEditorPathPolyline(path, { steps: 24 });
  if (poly.length < 3) return null;
  const pad = Math.max(0, Math.round(Number(opts.padding) || 2));
  const xs = poly.map((p) => p.x);
  const ys = poly.map((p) => p.y);
  const minX = Math.floor(Math.min(...xs)) - pad;
  const minY = Math.floor(Math.min(...ys)) - pad;
  const maxX = Math.ceil(Math.max(...xs)) + pad;
  const maxY = Math.ceil(Math.max(...ys)) + pad;
  const w = Math.max(1, maxX - minX + 1);
  const h = Math.max(1, maxY - minY + 1);
  const local = poly.map((p) => ({ x: p.x - minX, y: p.y - minY }));
  const { mask } = polylineToMask(w, h, local);
  const rgb = hexToRgb(opts.color || '#000000');
  const imageData = new ImageData(w, h);
  for (let i = 0; i < mask.length; i += 1) {
    if (!mask[i]) continue;
    const p = i * 4;
    imageData.data[p] = rgb.r;
    imageData.data[p + 1] = rgb.g;
    imageData.data[p + 2] = rgb.b;
    imageData.data[p + 3] = 255;
  }
  return { imageData, offsetX: minX, offsetY: minY };
}

/**
 * Count selected pixels (smoke / diagnostics).
 * @param {{ mask: Uint8Array }|null|undefined} sel
 * @returns {number}
 */
export function countMaskPixels(sel) {
  if (!sel?.mask) return 0;
  let n = 0;
  for (let i = 0; i < sel.mask.length; i += 1) {
    if (sel.mask[i]) n += 1;
  }
  return n;
}
